import { Breadcrumb, BreadcrumbLink, BreadcrumbPage } from '@ads/components-react';
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  PermissionsMatrix,
  useControllerList,
  useRolePermissions,
  useSavePermissions,
} from '@/features/access-control';
import type {
  BatchPermissionViewModel,
  RolePermissionViewModel,
} from '@/shared/api/generated/types';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

export const Route = createFileRoute('/_authenticated/access-management/$roleId/')({
  component: RolePermissionsPage,
});

export function RolePermissionsPage() {
  const { t } = useTranslation('accessControl');
  const { roleId } = Route.useParams();
  const navigate = useNavigate();

  const roleIdNum = Number(roleId);
  const isValidRoleId = Number.isFinite(roleIdNum);

  // ✅ Hooks SEMPRE chamados
  const { summary, roleName, isLoading: isLoadingSummary } = useRolePermissions(roleIdNum);
  const { controllers, isLoading: isLoadingControllers } = useControllerList();

  const { save, isPending: isSaving } = useSavePermissions();

  // A matriz só está pronta quando controllers (fonte das linhas) e summary
  // (permissões já atribuídas) terminarem de carregar.
  const isLoading = isLoadingControllers || isLoadingSummary;

  function handleSave(batch: BatchPermissionViewModel[]) {
    const permissonController: RolePermissionViewModel[] = batch.map(
      ({ roleId: _r, ...rest }) => rest,
    );
    save({ roleId: roleIdNum, permissonController });
  }

  function handleBack() {
    void navigate({ to: '/access-management' });
  }

  // ✅ redirects DEPOIS dos hooks
  if (!isValidRoleId) {
    return <Navigate to="/access-management" replace />;
  }

  return (
    <div className="flex flex-col gap-lg">
      <PageHeader
        title={t('permissions.title')}
        subtitle={t('permissions.subtitle')}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink asChild>
              <Link to="/access-management">{t('roles.title')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{roleName}</BreadcrumbPage>
          </Breadcrumb>
        }
      />

      <PermissionsMatrix
        roleId={roleIdNum}
        controllers={controllers}
        summary={summary}
        isLoading={isLoading}
        isSaving={isSaving}
        onSave={handleSave}
        onBack={handleBack}
      />
    </div>
  );
}
